import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  TextField,
  Select,
  InputAdornment,
  MenuItem,
  Button,
  CircularProgress,
  Dialog
} from '@mui/material';
import { withScoutTheme } from '../../ThemeContext/ThemeContext';
import useStyles from '../AdEditor/useStyles';
import useSharedStyles from '../AdEditor/useStyles';
import axios from 'axios';
import PropTypes from 'prop-types';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatDate } from 'react-day-picker/moment';

const budgetTypes = [
  ['One time', 'one_time'],
  ['Monthly Average*', 'monthly']
];

function SponsoredJobEditor({ sponsoredAd, apiKey, accessToken, jobId }) {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [costPerClick, setCostPerClick] = useState(0.25);
  const [isEndDate, setIsEndDate] = useState(!!sponsoredAd.end_date);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  const [budgetType, setBudgetType] = useState(
    budgetTypes.find((budget) => budget[1] === sponsoredAd.budget_type) || budgetTypes[0]
  );
  const [budget, setBudget] = useState(sponsoredAd.budget_amount);
  const [hireCount, setHireCount] = useState(sponsoredAd.hires_number);
  const [endDate, setEndDate] = useState(sponsoredAd.end_date || '');

  async function handleSubmit() {
    setIsSubmitting(true);
    try {
      await axios.put(
        `/api/v4/indeed/job_ads/${sponsoredAd.job_ad_id}/sponsored_job`,
        {
          indeed_access_token: accessToken,
          sponsored_job: {
            budget_type: budgetType[1],
            budget_amount: budget,
            hires_number: hireCount,
            end_date: endDate ? formatDate(endDate, 'YYYY-MM-DD') : null
          }
        },
        {
          headers: {
            'X-api-authenticate': apiKey
          }
        }
      );
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
      setIsSuccessDialogOpen(true);
    }
  }

  const classes = useStyles();
  const sharedClasses = useSharedStyles();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.containerOuter}>
      <div className={classes.container}>
        <div className={classes.stepContainer}>
          <h4>Sponsor Job</h4>
          <span className={classes.sponsorJobSubTitle}>
            Choosing the <b>recommended budget</b> means your listing will get{' '}
            <b>better visibility</b> and show up more often in search results, making it easier for
            relevant job seekers to find and apply to your job.
            <br />
            <br />
            All currency values will use the currency defined in your Indeed account.
          </span>

          <div>
            <div className={`${classes.previewType} ${isAdvanced ? 'mobile' : ''}`}>
              <div onClick={() => setIsAdvanced(false)}>Simple</div>
              <div
                onClick={() => {
                  setIsAdvanced(true);
                  setBudgetType(budgetTypes[0]);
                }}
              >
                Advanced
              </div>
            </div>

            <Grid container className={classes.sponsorJobContainer}>
              {isAdvanced ? (
                <>
                  <div className={classes.formControl}>
                    <h4 className={classes.paragraphTitle}>Job Budget</h4>
                    <div className={classes.paragraph}>
                      Instead of paying a large amount up-front to post your job, choose a small
                      amount you are willing to pay each time a job seeker clicks to view your job
                      (the Maximum cost per click or Max CPC).
                      <ul>
                        <li>Most clicks on Indeed cost between $0.20 - $1.20 (USD)</li>
                        <li>Jobs with higher Max CPCs tend to get seen more often</li>
                        <li>You can change your Max CPC at any time</li>
                      </ul>
                    </div>
                  </div>
                  <FormControl
                    fullWidth={true}
                    className={`${classes.formControl} ${classes.medium}`}
                  >
                    <FormLabel required={true} className={classes.label}>
                      Maximum cost per click
                    </FormLabel>
                    <Grid
                      container
                      classes={{ root: `${classes.controlledNumberInput} ${classes.budgetInput}` }}
                    >
                      <TextField
                        className={`${classes.textField} ${classes.inputAdornment}`}
                        variant="outlined"
                        value={costPerClick}
                        placeholder="0"
                        InputProps={{
                          classes: { focused: classes.budgetInputFocused },
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        onChange={(e) => {
                          if (!e.target.value) setCostPerClick(null);
                          else if (/^\d+(\.\d{0,2})?$/.test(e.target.value))
                            setCostPerClick(e.target.value);
                        }}
                      />
                    </Grid>
                  </FormControl>
                  <FormControl
                    fullWidth={true}
                    className={`${classes.formControl} ${classes.medium}`}
                  >
                    <FormLabel required={true} className={classes.label}>
                      Job budget
                    </FormLabel>
                    <div className={classes.subLabel}>
                      Choose the total amount you want to spend promoting your job on Indeed. You
                      can change your job budget at any time.
                    </div>
                    <Grid
                      container
                      classes={{ root: `${classes.controlledNumberInput} ${classes.budgetInput}` }}
                    >
                      <TextField
                        className={`${classes.textField} ${classes.inputAdornment}`}
                        variant="outlined"
                        value={Number(budget).toLocaleString()}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        onChange={(e) =>
                          setBudget(
                            e.target.value ? parseFloat(e.target.value.split(',').join('')) : 0
                          )
                        }
                      />
                    </Grid>
                  </FormControl>
                  <span className={classes.mediumText}>
                    You&apos;ll get at least {parseInt(budget / costPerClick)} clicks on your job if
                    you use your entire budget.
                  </span>
                </>
              ) : (
                <>
                  <Grid item xs={8}>
                    <FormControl fullWidth={true} className={`${classes.formControl}`}>
                      <FormLabel focused={false} required={true} className={classes.label}>
                        How many people do you need to hire?
                      </FormLabel>
                      <Grid className={classes.hireCounter}>
                        <IconButton
                          disabled={hireCount <= 0}
                          onClick={() => setHireCount(hireCount - 1)}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <div>{hireCount}</div>
                        <IconButton onClick={() => setHireCount(hireCount + 1)}>
                          <AddIcon />
                        </IconButton>
                      </Grid>
                    </FormControl>

                    <FormControl fullWidth={true} className={classes.formControl}>
                      <FormLabel focused={false} className={classes.label}>
                        Job budget
                      </FormLabel>
                      <span className={classes.subLabel}>
                        Recommended budget (value) based on your job title and location.
                      </span>
                      <Grid
                        container
                        classes={{
                          root: `${classes.controlledNumberInput} ${classes.budgetInput}`
                        }}
                      >
                        <TextField
                          className={`${classes.textField} ${classes.inputAdornment}`}
                          variant="outlined"
                          value={Number(budget).toLocaleString()}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          onChange={(e) =>
                            setBudget(
                              e.target.value ? parseFloat(e.target.value.split(',').join('')) : 0
                            )
                          }
                        />
                        <Select
                          classes={{ select: classes.input, selectMenu: classes.menuItem }}
                          variant="outlined"
                          value={budgetType}
                          onChange={(e) => setBudgetType(e.target.value)}
                          displayEmpty={true}
                          renderValue={(value) => value[0]}
                        >
                          {budgetTypes.map((type) => (
                            <MenuItem
                              classes={{ root: classes.menuItem }}
                              key={type[1]}
                              value={type}
                            >
                              {type[0]}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <span className={classes.smallText}>
                        You can change the amount, pause or close your job at any time.
                      </span>
                    </FormControl>
                  </Grid>
                  {/* <Grid xs={4}>
                      <div className={classes.estimatedResults}>
                        <h4>ESTIMATED RESULTS</h4>
                        <h3><b>(TEST)</b> applicants</h3>
                        <span>over the next (TEST) weeks**</span>
                      </div>
                    </Grid> */}
                </>
              )}
              <Grid container style={{ marginTop: 35 }}>
                <FormControlLabel
                  label="Set an end date (optional)"
                  control={
                    <Checkbox checked={isEndDate} onChange={() => setIsEndDate(!isEndDate)} />
                  }
                />
              </Grid>
              {isEndDate && (
                <DayPickerInput
                  value={endDate}
                  onDayChange={(day) => setEndDate(formatDate(day, 'LL'))}
                  classNames={{
                    container: `${classes.calendarContainer} ${classes.small}`,
                    overlay: {}
                  }}
                  format="LL"
                  placeholder="Select a date"
                />
              )}
              <Grid container style={{ marginTop: 15 }} direction="column">
                <span className={classes.smallText}>* Actual daily spend may vary.</span>
                <span className={classes.smallText}>
                  ** Based on Indeed&apos;s past results. This is not a guarantee of future
                  performance.
                </span>
              </Grid>
            </Grid>
            <div className={classes.submitButtonContainer}>
              <Button
                disableElevation
                color="secondary"
                onClick={handleSubmit}
                disabled={!budget || !hireCount}
              >
                Sponsor job
                {isSubmitting && (
                  <CircularProgress style={{ marginLeft: 6 }} size={20} color="inherit" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={isSuccessDialogOpen}
        onClose={() => window.history.back()}
        classes={{ paper: classes.successDialog }}
      >
        <div className={classes.successCheck}>
          <CheckCircleIcon color="primary" fontSize="large" />
        </div>
        <h4>Success</h4>
        <span>Your ad has been sponsored</span>
        <div className={classes.dialogButtonContainer} style={{ marginTop: 20 }}>
          <Button
            disableElevation
            color="secondary"
            className={sharedClasses.buttonSecondary}
            onClick={() => (window.location = `/admin/jobs/${jobId}/job_ads`)}
          >
            Back to ads
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

SponsoredJobEditor.propTypes = {
  sponsoredAd: PropTypes.object.isRequired,
  apiKey: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired,
  jobId: PropTypes.number.isRequired
};

export default withScoutTheme(SponsoredJobEditor);
